/**
 * LZ Compress Tool
 *
 * Compress text using LZ-string compression. Shows compression ratio.
 */

import LZString from 'lz-string';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const LzCompressTool: ITransformerTool = {
  id: 'lz-compress',
  name: 'LZ Compress',
  description: 'Compress text using LZ-string compression with compression ratio',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['lz', 'lz-string', 'compress', 'compression', 'lzw'],

  seo: {
    slug: 'lz-string-compress',
    pageTitle: 'LZ-String Compress - Free Online Tool | Prism',
    metaDescription: 'Compress text using LZ-string algorithm. Choose base64, URI-safe, or UTF-16 output. See compression ratio. Fast, private, runs in your browser.',
    keywords: ['lz-string compress', 'lz compress online', 'text compression', 'lzw compression', 'compress string'],
    relatedTools: ['lz-decompress', 'base64-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'outputFormat',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'base64',
      options: ['base64', 'uri', 'utf16', 'raw'],
      description: 'Output encoding format for the compressed data',
    },
    {
      name: 'showStats',
      type: 'boolean',
      label: 'Show Stats',
      defaultValue: true,
      description: 'Show compression statistics (ratio, sizes)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const format = String(config.outputFormat ?? 'base64');
    const showStats = config.showStats !== false;
    const text = String(input ?? '');
    if (!text) return '';

    try {
      let compressed: string;

      switch (format) {
        case 'base64':
          compressed = LZString.compressToBase64(text);
          break;
        case 'uri':
          compressed = LZString.compressToEncodedURIComponent(text);
          break;
        case 'utf16':
          compressed = LZString.compressToUTF16(text);
          break;
        case 'raw':
          compressed = LZString.compress(text);
          break;
        default:
          compressed = LZString.compressToBase64(text);
      }

      if (!showStats) return compressed;

      const originalSize = new Blob([text]).size;
      const compressedSize = new Blob([compressed]).size;
      const ratio = originalSize > 0 ? ((1 - compressedSize / originalSize) * 100).toFixed(1) : '0.0';

      return [
        `--- Compression Stats ---`,
        `Format:          ${format}`,
        `Original:        ${originalSize.toLocaleString()} bytes`,
        `Compressed:      ${compressedSize.toLocaleString()} bytes`,
        `Ratio:           ${ratio}% reduction`,
        ``,
        `--- Compressed Output ---`,
        compressed,
      ].join('\n');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to compress';
      throw ToolError.execution(message);
    }
  },
};
